import { z } from "zod";
import { AccountId } from "./ids.js";
/**
 * The sync service's wire formats (architecture §6.3): what Pub/Sub, Gmail and Microsoft Graph
 * push to it, and the jobs it queues for itself on `sync-jobs`. Echo expectations are
 * `OperationDoc.expectedState` (mail-api.ts), written by the api's mail actions.
 */
/** A Pub/Sub push request body. `data` is base64. */
export const PubSubPush = z.object({
    message: z.object({
        data: z.string(),
        messageId: z.string(),
        publishTime: z.string().optional(),
        attributes: z.record(z.string(), z.string()).optional(),
    }),
    subscription: z.string(),
});
/** What Gmail publishes to `gmail-push` after `users.watch` (historyId arrives as a number). */
export const GmailPushData = z.object({
    emailAddress: z.string().email(),
    historyId: z.union([z.string().regex(/^\d+$/), z.number().int().nonnegative()]).transform(String),
});
export const SyncReason = z.enum([
    "gmail-push",
    "graph-notification",
    "graph-missed",
    "subscription-recreated",
    "poll",
    "connected",
    /** The worker's own: a full sync ran out of its time budget and carries on in a new job. */
    "full-sync-continue",
]);
export const GraphSubscriptionEvent = z.enum(["changed", "reauthorizationRequired", "subscriptionRemoved", "missed"]);
/**
 * The Workspace Events types the meeting-notes subscription asks for (architecture §11), plus the
 * subscription lifecycle events Google sends on its own.
 */
export const MEET_TRANSCRIPT_GENERATED = "google.workspace.meet.transcript.v2.fileGenerated";
export const MEET_CONFERENCE_ENDED = "google.workspace.meet.conference.v2.ended";
export const MEET_SUBSCRIBED_EVENTS = [MEET_TRANSCRIPT_GENERATED, MEET_CONFERENCE_ENDED];
export const MeetEventType = z.enum([
    MEET_TRANSCRIPT_GENERATED,
    MEET_CONFERENCE_ENDED,
    "google.workspace.events.subscription.v1.expirationReminder",
    "google.workspace.events.subscription.v1.expired",
    "google.workspace.events.subscription.v1.suspended",
]);
/**
 * One message on `sync-jobs`. `account` syncs one account; `graph-subscription` comes from the
 * public Graph webhook, which knows only the subscription id (it holds no Firestore access).
 * `historyId` is the Gmail push's: a job whose history the account already passed is a no-op.
 */
export const SyncJob = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("account"),
        accountId: AccountId,
        reason: SyncReason,
        historyId: z.string().nullable().default(null),
        requestId: z.string().min(1),
    }),
    z.object({
        kind: z.literal("graph-subscription"),
        subscriptionId: z.string().min(1),
        event: GraphSubscriptionEvent,
        requestId: z.string().min(1),
    }),
    /** A Workspace Events delivery from `meet-events` (WP-017), resolved by the worker. */
    z.object({
        kind: z.literal("meet-event"),
        /** `subscriptions/{id}` (from the CloudEvent's `ce-source`). */
        subscription: z.string().min(1),
        type: MeetEventType,
        /** `conferenceRecords/{c}/transcripts/{t}` or `conferenceRecords/{c}`; null for lifecycle events. */
        resource: z.string().min(1).nullable(),
        requestId: z.string().min(1),
    }),
    /** Make (or retry) a meeting's notes: after a transcript event, or from the fallback cron. */
    z.object({
        kind: z.literal("meeting-notes"),
        meetingId: z.string().min(1),
        /** Notes from this conference; null = the latest ended conference with a transcript. */
        conferenceRecord: z.string().min(1).nullable().default(null),
        reason: z.enum(["transcript", "fallback"]),
        requestId: z.string().min(1),
    }),
]);
/** A Graph change notification batch (`notificationUrl`). */
export const GraphNotifications = z.object({
    value: z
        .array(z.object({
        subscriptionId: z.string().min(1),
        clientState: z.string().nullable().optional(),
        changeType: z.string().optional(),
        resource: z.string().optional(),
    }))
        .min(1),
});
/** A Graph lifecycle notification batch (`lifecycleNotificationUrl`). */
export const GraphLifecycleNotifications = z.object({
    value: z
        .array(z.object({
        subscriptionId: z.string().min(1),
        clientState: z.string().nullable().optional(),
        lifecycleEvent: z.enum(["reauthorizationRequired", "subscriptionRemoved", "missed"]),
    }))
        .min(1),
});
//# sourceMappingURL=sync.js.map